import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  getHighestRole,
  can,
  hasDashboardAccess,
  isSystemAdmin,
  type UserProfile,
} from '../../api/profile';
import { SideDrawer } from '../../shared/ui';

const FLYOUT_THRESHOLD = 3;
const DESKTOP_MQ = '(min-width: 992px)';

type NavItem = {
  path: string;
  label: string;
  icon: string;
  end?: boolean;
};

type NavSection = {
  id: string;
  title: string;
  icon: string;
  items: NavItem[];
};

type DashboardSidebarProps = {
  open: boolean;
  profile: UserProfile;
  onClose: () => void;
  onSignOut: () => void;
};

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(DESKTOP_MQ).matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

function buildSections(profile: UserProfile): NavSection[] {
  type CandidateItem = NavItem & { show: boolean };
  type CandidateSection = Omit<NavSection, 'items'> & { items: CandidateItem[] };

  const sections: CandidateSection[] = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: 'bi-speedometer2',
      items: [
        {
          path: '/dashboard',
          end: true,
          label: 'Overview',
          icon: 'bi-speedometer2',
          show: hasDashboardAccess(profile),
        },
      ],
    },
    {
      id: 'operations',
      title: 'Operations',
      icon: 'bi-kanban',
      items: [
        { path: '/dashboard/events', label: 'Events', icon: 'bi-calendar-event', show: can(profile, 'events', 'view') },
        { path: '/dashboard/projects', label: 'Projects', icon: 'bi-folder', show: can(profile, 'projects', 'view') },
        { path: '/dashboard/committee', label: 'Committee', icon: 'bi-people-fill', show: can(profile, 'committee', 'view') },
        { path: '/dashboard/timeline', label: 'Timeline', icon: 'bi-clock-history', show: can(profile, 'timeline', 'view') },
      ],
    },
    {
      id: 'website',
      title: 'Website',
      icon: 'bi-globe2',
      items: [
        { path: '/dashboard/notices', label: 'Important Notices', icon: 'bi-megaphone', show: can(profile, 'notices', 'view') },
        { path: '/dashboard/classes', label: 'Classes', icon: 'bi-book', show: can(profile, 'classes', 'view') },
        { path: '/dashboard/gallery', label: 'Gallery', icon: 'bi-images', show: can(profile, 'gallery', 'view') },
        { path: '/dashboard/reviews', label: 'Reviews', icon: 'bi-chat-quote', show: can(profile, 'reviews', 'view') },
        {
          path: '/dashboard/contact-details',
          label: 'Contact Details',
          icon: 'bi-person-lines-fill',
          show: can(profile, 'contactSettings', 'view'),
        },
        { path: '/dashboard/donors', label: 'Donors', icon: 'bi-heart-fill', show: can(profile, 'contactSettings', 'view') },
      ],
    },
    {
      id: 'administration',
      title: 'Administration',
      icon: 'bi-shield-lock',
      items: [
        { path: '/dashboard/users', label: 'User Management', icon: 'bi-people', show: can(profile, 'users', 'view') },
        { path: '/dashboard/roles', label: 'Roles', icon: 'bi-shield-lock', show: can(profile, 'roles', 'view') },
        { path: '/dashboard/settings', label: 'Settings', icon: 'bi-gear', show: isSystemAdmin(profile) },
      ],
    },
  ];

  return sections
    .map((section) => ({
      id: section.id,
      title: section.title,
      icon: section.icon,
      items: section.items
        .filter((item) => item.show)
        .map(({ show: _show, ...item }) => item),
    }))
    .filter((section) => section.items.length > 0);
}

function pathMatches(pathname: string, item: NavItem): boolean {
  if (item.end) return pathname === item.path;
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

const SidebarLink: React.FC<{
  item: NavItem;
  onNavigate: () => void;
  variant?: 'default' | 'sub';
}> = ({ item, onNavigate, variant = 'default' }) => (
  <NavLink
    to={item.path}
    end={item.end}
    className={({ isActive }) =>
      [
        'dashboard-sidebar__link',
        variant === 'sub' ? 'dashboard-sidebar__link--sub' : '',
        isActive ? 'dashboard-sidebar__link--active' : '',
      ]
        .filter(Boolean)
        .join(' ')
    }
    onClick={onNavigate}
  >
    <i className={`bi ${item.icon}`} aria-hidden />
    <span>{item.label}</span>
  </NavLink>
);

const SidebarSection: React.FC<{
  section: NavSection;
  isDesktop: boolean;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNavigate: () => void;
}> = ({ section, isDesktop, expandedId, onToggle, onNavigate }) => {
  const location = useLocation();
  const usesPanel = section.items.length > FLYOUT_THRESHOLD;
  const isExpanded = expandedId === section.id;
  const hasActiveChild = section.items.some((item) => pathMatches(location.pathname, item));
  const panelAsFlyout = isDesktop;

  if (!usesPanel) {
    return (
      <div className="dashboard-sidebar__group">
        <div className="dashboard-sidebar__group-title">{section.title}</div>
        <div className="dashboard-sidebar__group-links">
          {section.items.map((item) => (
            <SidebarLink key={item.path} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        'dashboard-sidebar__group',
        'dashboard-sidebar__group--collapsible',
        panelAsFlyout ? 'dashboard-sidebar__group--flyout' : 'dashboard-sidebar__group--accordion',
        isExpanded ? 'dashboard-sidebar__group--open' : '',
        hasActiveChild ? 'dashboard-sidebar__group--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className={`dashboard-sidebar__group-toggle ${
          hasActiveChild || isExpanded ? 'dashboard-sidebar__group-toggle--active' : ''
        }`}
        aria-expanded={isExpanded}
        aria-controls={`sidebar-section-${section.id}`}
        onClick={() => onToggle(isExpanded ? '' : section.id)}
      >
        <span className="dashboard-sidebar__group-toggle-label">
          <i className={`bi ${section.icon}`} aria-hidden />
          {section.title}
        </span>
        <i
          className={`bi ${
            panelAsFlyout
              ? 'bi-chevron-left'
              : isExpanded
                ? 'bi-chevron-up'
                : 'bi-chevron-down'
          } dashboard-sidebar__chevron`}
          aria-hidden
        />
      </button>

      {isExpanded && (
        <div
          id={`sidebar-section-${section.id}`}
          className={panelAsFlyout ? 'dashboard-sidebar__flyout' : 'dashboard-sidebar__accordion'}
          role="region"
          aria-label={section.title}
        >
          {panelAsFlyout && (
            <div className="dashboard-sidebar__flyout-title">{section.title}</div>
          )}
          <div className={panelAsFlyout ? 'dashboard-sidebar__flyout-links' : 'dashboard-sidebar__accordion-links'}>
            {section.items.map((item) => (
              <SidebarLink
                key={item.path}
                item={item}
                onNavigate={onNavigate}
                variant={panelAsFlyout ? 'default' : 'sub'}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  open,
  profile,
  onClose,
  onSignOut,
}) => {
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const sections = buildSections(profile);

  useEffect(() => {
    if (!open) {
      setExpandedId(null);
      return;
    }

    const activeSection = buildSections(profile).find(
      (section) =>
        section.items.length > FLYOUT_THRESHOLD &&
        section.items.some((item) => pathMatches(location.pathname, item))
    );
    setExpandedId(activeSection?.id ?? null);
  }, [open, location.pathname, profile]);

  return (
    <SideDrawer
      id="dashboard-sidebar"
      open={open}
      onClose={onClose}
      ariaLabel="Dashboard menu"
      className={expandedId && isDesktop ? 'side-drawer--flyout-open' : ''}
      header={
        <>
          <div className="dashboard-sidebar__user">
            <span className="dashboard-sidebar__user-name">{profile.name}</span>
            <span className="dashboard-sidebar__user-role">
              <i className="bi bi-shield-check" /> {getHighestRole(profile)}
            </span>
          </div>
          <button
            type="button"
            className="side-drawer__close"
            aria-label="Close menu"
            onClick={onClose}
          >
            <i className="bi bi-x-lg" />
          </button>
        </>
      }
      footer={
        <>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="dashboard-sidebar__home"
            title="Open public website"
            aria-label="Home"
            onClick={onClose}
          >
            <i className="bi bi-house-door" aria-hidden />
          </a>
          <button type="button" className="dashboard-sidebar__signout" onClick={onSignOut}>
            <i className="bi bi-box-arrow-right" aria-hidden />
            <span>Sign Out</span>
          </button>
        </>
      }
    >
      <nav className="dashboard-sidebar__nav" aria-label="Dashboard">
        {sections.map((section) => (
          <SidebarSection
            key={section.id}
            section={section}
            isDesktop={isDesktop}
            expandedId={expandedId}
            onToggle={(id) => setExpandedId(id || null)}
            onNavigate={onClose}
          />
        ))}
      </nav>
    </SideDrawer>
  );
};

export default DashboardSidebar;
